import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SiteController } from '../../lib/site-controllers';
import type { Site } from '../../lib/sites';
import SiteControllerControl from '../site-controllers/SiteControllerControl';
import SiteBrandingLogo from '../SiteBrandingLogo';
import LocationControl from './LocationControl';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';

interface SitesControlData {
  siteId: string;
  siteName: string;
  controllers: SiteController[];
}

interface SitesControlProps {
  allControllers: SitesControlData[];
  sites?: Site[];
  onSetCurrentPreset: (areaId: string, presetId: string | null, presetAddress?: number | null) => void;
}

type SitesControlView = 'controllers' | 'map';

export default function SitesControl({ allControllers, sites, onSetCurrentPreset }: SitesControlProps) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [view, setView] = useState<SitesControlView>('controllers');
  const canConfigure = currentUser?.role === 'super_user' || currentUser?.role === 'site_admin';

  const siteById = useMemo(() => {
    const map = new Map<string, Site>();
    (sites ?? []).forEach((s) => map.set(s.id, s));
    return map;
  }, [sites]);
  
  const controllerEntries = useMemo(() => {
    if (!allControllers) return [];
    return allControllers.flatMap(({ siteId, siteName, controllers }) =>
      controllers.map((controller) => ({ siteId, siteName, controller }))
    );
  }, [allControllers]);

  return (
    <div className="mb-6 space-y-4">
      <Tabs
        value={view}
        onValueChange={(v) => setView(v as SitesControlView)}
        className="w-full space-y-4"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
          <h2 className="text-xl font-semibold sm:order-1">Sites</h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3 sm:order-2">
            <TabsList className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
              <TabsTrigger value="controllers">Controllers</TabsTrigger>
              <TabsTrigger value="map">Map</TabsTrigger>
            </TabsList>
            {currentUser?.role !== 'end_user' && (
              <Button variant="outline" onClick={() => navigate('/sites')} className="shrink-0 w-full sm:w-auto">
                View All Sites
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="controllers" className="mt-0 space-y-4 focus-visible:ring-0 focus-visible:ring-offset-0">
          {controllerEntries.length > 0 ? (
            <div className="space-y-4">
              {controllerEntries.map(({ siteId, siteName, controller }) => (
                <div key={controller.id} className="w-full">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-4 w-4 text-primary shrink-0" />
                      <span className="truncate text-sm font-semibold">{siteName}</span>
                    </div>
                    <SiteBrandingLogo site={siteById.get(siteId)} />
                  </div>
                  <SiteControllerControl
                    controller={controller}
                    siteId={siteId}
                    onSetCurrentPreset={onSetCurrentPreset}
                    canConfigure={canConfigure}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center border rounded-md text-muted-foreground">
              No controllers found for your sites
            </div>
          )}
        </TabsContent>
        <TabsContent value="map" className="mt-0 focus-visible:ring-0 focus-visible:ring-offset-0">
          <LocationControl mapHeightClassName="min-h-[18rem] h-[26rem]" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
